import Hls from "hls.js";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { useI18n } from "../i18n.js";

/**
 * Il riquadro video.
 *
 * La sorgente e' un flusso HLS servito dal NOSTRO server: l'URL della camera,
 * che contiene le credenziali, non arriva mai fin qui. Il ritardo e' di
 * qualche secondo — l'allarme lo fa scattare il sensore, non l'occhio di chi
 * guarda — e in cambio tutto passa da un solo confine di autenticazione.
 *
 * `hls.js` serve a Chrome e Firefox, che l'HLS non lo leggono da soli. Safari
 * e iOS lo riproducono nativamente, e li' si passa l'URL al `<video>` senza
 * intermediari: caricare comunque la libreria funzionerebbe, ma rinuncerebbe
 * al decodificatore hardware del sistema.
 */
export function VideoPlayer({
  src,
  label,
  onPlayingChange,
  children,
}: {
  src?: string | undefined;
  label?: string | undefined;
  /** Vero solo quando arrivano davvero dei fotogrammi.
   *
   *  Serve al badge LIVE della scheda: un URL configurato NON e' un video che
   *  arriva. La camera puo' essere spenta, irraggiungibile, o il
   *  ritrasmettitore puo' non essersi ancora collegato — e un rettangolo nero
   *  con scritto LIVE sopra e' esattamente il tipo di bugia che questa
   *  interfaccia non deve dire. */
  onPlayingChange?: ((playing: boolean) => void) | undefined;
  /** Sovrapposizioni ancorate al riquadro del video: il marker del laser
   *  legge di qui le proporzioni vere del fotogramma, che sono quelle su cui
   *  la sua posizione e' una frazione. */
  children?: ReactNode;
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const video = ref.current;
    if (!video || !src) {
      onPlayingChange?.(false);
      return;
    }
    setError("");

    // `playing` scatta al primo fotogramma decodificato, `waiting` e `stalled`
    // quando il flusso si interrompe: e' la differenza fra "sta arrivando" e
    // "c'e' un URL".
    const up = () => onPlayingChange?.(true);
    const down = () => onPlayingChange?.(false);
    video.addEventListener("playing", up);
    video.addEventListener("waiting", down);
    video.addEventListener("stalled", down);
    video.addEventListener("emptied", down);

    const detach = () => {
      video.removeEventListener("playing", up);
      video.removeEventListener("waiting", down);
      video.removeEventListener("stalled", down);
      video.removeEventListener("emptied", down);
      onPlayingChange?.(false);
    };

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      return () => {
        detach();
        video.removeAttribute("src");
        video.load();
      };
    }

    if (!Hls.isSupported()) {
      setError(t("hdr.noSignal"));
      return detach;
    }

    const hls = new Hls({
      // La camera puo' non essere ancora collegata: MediaMTX si attacca a
      // richiesta, quindi i primi manifesti possono mancare. Ritentare invece
      // di arrendersi e' la differenza fra "video assente" e "un attimo".
      manifestLoadingMaxRetry: 6,
      manifestLoadingRetryDelay: 1000,
      levelLoadingMaxRetry: 6,
      fragLoadingMaxRetry: 6,
      lowLatencyMode: true,
      // Non accumulare: davanti a un impianto si guarda cosa succede ADESSO,
      // e un buffer generoso e' ritardo travestito da fluidita'.
      backBufferLength: 10,
    });
    hls.loadSource(src);
    hls.attachMedia(video);

    hls.on(Hls.Events.ERROR, (_e, data) => {
      if (!data.fatal) return;
      // Gli errori fatali di rete e di media sono recuperabili: hls.js sa
      // ripartire. Solo il terzo caso e' definitivo, e va detto invece di
      // lasciare un rettangolo nero che sembra una camera spenta.
      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
      else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
      else {
        setError(t("card.streamLost"));
        onPlayingChange?.(false);
        hls.destroy();
      }
    });

    return () => {
      detach();
      hls.destroy();
    };
  }, [src, t, onPlayingChange]);

  return (
    <div className="video">
      {src ? (
        <video ref={ref} autoPlay muted playsInline controls={false} />
      ) : (
        <div className="video__empty">{t("hdr.noSignal")}</div>
      )}
      {error && <div className="video__error">{error}</div>}
      {label && <span className="video__label">{label}</span>}
      {children}
    </div>
  );
}
